import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAudit, useAuditSummary } from "@/features/audits/api";
import { AuditStatusBadge } from "@/features/audits/badges";
import { AuditSummaryKpis } from "@/features/audits/AuditSummaryKpis";

export const Route = createFileRoute("/_authenticated/audits/$auditId")({
  component: AuditDetailLayout,
});

function AuditDetailLayout() {
  const { auditId } = Route.useParams();
  const { data: audit, isLoading } = useAudit(auditId);
  const { data: summary } = useAuditSummary(auditId);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  if (isLoading) return <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!audit) return <div className="py-20 text-center text-muted-foreground">Audit not found.</div>;

  const tabs = [
    { label: "Overview", to: `/audits/${auditId}` },
    { label: "Execute", to: `/audits/${auditId}/execute` },
    { label: "Discrepancies", to: `/audits/${auditId}/discrepancies` },
    { label: "Sign-Off", to: `/audits/${auditId}/signoff` },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <Button variant="ghost" size="sm" className="mb-2 -ml-2 text-muted-foreground" asChild>
            <Link to="/audits"><ArrowLeft className="mr-2 h-4 w-4" /> All audits</Link>
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">{audit.audit_name}</h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
            <AuditStatusBadge status={audit.status} />
            <span>· {audit.audit_type}</span>
            {audit.start_date && <span>· {audit.start_date} → {audit.end_date ?? "—"}</span>}
          </div>
        </div>
      </div>

      <AuditSummaryKpis summary={summary} />

      <div className="flex flex-wrap gap-1 border-b">
        {tabs.map((t) => {
          const active = pathname === t.to;
          return (
            <Link
              key={t.to} to={t.to}
              className={`-mb-px border-b-2 px-3 py-2 text-sm ${active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            >{t.label}</Link>
          );
        })}
      </div>

      <Outlet />
    </div>
  );
}
